import {
	Activity,
	Category,
	UserStatus,
	UserStatusModel,
	Reward,
	Levels,
	Level,
	StickerCollection,
	Sticker,
	CollectionsGroup,
	LevelCombo,
	LevelComboList,
	ExchangeDiamondsRating,
	ExchangeDiamondsRatingModel,
	AutoclickerDiamondsRating,
	AutoclickerDiamondsRatingModel,
	Product,
	Skin,
	SkinImage,
} from '@/core/interfaces';

/**
 * Maps a UserStatusModel object to a UserStatus object.
 *
 * @param {UserStatusModel} user - The input user status model containing various properties such as generated coins, diamonds,
 * card categories, rewards, levels, collections, game configurations, and more.
 *
 * @returns {UserStatus} A new UserStatus object with mapped properties including new coins and diamonds collected, categories,
 * rewards, youtube rewards, levels, products, email, total coins earned, pending actions, collections, friends rewards,
 * skins, and configuration details.
 */
export const UserStatusMapper = ({
	generatedCoins,
	generatedDiamonds,
	previousCoins,
	extraInfo,
	levelImageUrl,
	energyPerSecond,
	cardCategories,
	otherRewards,
	levels: levelsModel,
	dailyRewards,
	stickerCollections,
	otherRewardsAvailable,
	gameConfig,
	friendsRewardCoins,
	diamondRatings,
	diamondUnattended,
	diamondsForSell,
	skins: skinsModel,
}: UserStatusModel): UserStatus => {
	/** Categories ***/
	let categories: Category[] = [];
	if (cardCategories.length > 0) {
		cardCategories.map((item) => {
			let activities: Activity[] = [];
			if (item.cards) {
				item.cards.map((item) => {
					activities.push({
						id: item.id,
						category: item.category,
						image:
							item.imageUrl ||
							process.env.VITE_PLACEHOLDER_ACTIVITY_IMAGE!,
						isNew: item.isNew,
						thumbnail: {
							title: item.thumbnail.title,
							description: item.thumbnail.description,
						},
						detail: {
							title: item.detail.title,
							description: item.detail.description,
						},
						initialPrice: item.initialPrice,
						dependencyCard: item.dependencyCard,
						userInfo: {
							accumulatedRewards:
								item.userInfo?.accumulatedRewards,
							currentLevel: item.userInfo?.currentLevel,
							nextLevelPrice: item.userInfo?.nextLevelPrice,
							nextLevelReward: item.userInfo?.nextLevelReward,
							timerRemainingSeconds:
								item.userInfo?.timerRemainingSeconds,
						},
						buyOnlyOnce: item.buyOnlyOnce,
						expired: item.expired,
					});
				});
			}

			categories.push({
				id: item.id,
				name: item.name,
				description: item.description,
				activities: activities,
			});
		});
	}

	/** Rewards ***/
	let rewards: Reward[] = [];
	let youtubeRewards: Reward[] = [];
	if (otherRewards.length > 0) {
		otherRewards.map((reward) => {
			if (reward.isYoutube) {
				youtubeRewards.push({
					id: reward.id,
					title: reward.title,
					externalUrl: reward.externalUrl,
					image: reward.imageName,
					diamondsReward: reward.diamondsReward,
					reward: reward.reward,
					availableForCollecting: reward.availableForCollecting,
					requestedByUser: reward.requestedByUser,
					completed: reward.collected,
					isYoutube: reward.isYoutube,
				});
			} else {
				rewards.push({
					id: reward.id,
					title: reward.title,
					externalUrl: reward.externalUrl,
					image: reward.imageName,
					diamondsReward: reward.diamondsReward,
					reward: reward.reward,
					availableForCollecting: reward.availableForCollecting,
					requestedByUser: reward.requestedByUser,
					completed: reward.collected,
				});
			}
		});
	}

	/** Levels ***/
	let levelsArray: Level[] = [];
	let levelComboList: LevelComboList[] = [];
	if (levelsModel.levels.length > 0) {
		levelsModel.levels.map((item) => {
			let comboLevel: LevelCombo = {
				coinsReward: item.combo.coinsReward,
				diamondsReward: item.combo.diamondsReward,
				diamondsCostReveal: item.combo.diamondsPrice,
			};

			levelComboList.push({
				level: item.level,
				name: item.name,
				shurikenRewards: item.combo.coinsReward,
				diamondsRewards: item.combo.diamondsReward,
			});

			levelsArray.push({
				level: item.level,
				name: item.name,
				requiredPoints: item.requiredStars,
				earnByTap: item.earnByTap,
				maxEnergy: item.maxEnergy,
				image: item.imageUrl,
				bonus: item.referralBonus,
				diamondsReward: item.diamondsReward,
				combo: comboLevel,
			});
		});
	}
	let levels: Levels = {
		currentLevel: levelsModel.currentLevel,
		changed: levelsModel.changed,
		levels: levelsArray,
	};

	/** Products **/
	let products: Product[] = [];
	if (diamondsForSell.length > 0) {
		diamondsForSell.map((item) => {
			products.push({
				id: item.id,
				title: item.title,
				description: item.description,
				image: item.imageUrl,
				quantity: item.quantity,
				stars: item.stars,
				type: item.type,
			});
		});
	}

	/** Pending actions ***/
	let earnAction =
		dailyRewards.some((reward) => reward.availableToday) ||
		otherRewardsAvailable;
	const pendingActions = {
		buildAction: false,
		earnAction,
	};

	/** Collections ***/
	const groups: CollectionsGroup[] = [];
	stickerCollections.map((gr) => {
		let groupCollections: StickerCollection[] = [];

		gr.collections.map((item) => {
			const stickers: Sticker[] = [];

			let stickersCollected: number = 0;

			for (let index = 1; index <= item.numberOfTiles; index++) {
				const obtainedSticker = item.obtainedTiles.find(
					(sticker) => sticker.position === index
				);

				if (obtainedSticker) {
					stickers.push({
						position: obtainedSticker.position,
						image: obtainedSticker.imageUrl,
					});
					stickersCollected++;
				} else {
					stickers.push({
						position: index,
						image: process.env.VITE_COLLECTION_PATTERN_IMAGE!,
					});
				}
			}

			groupCollections.push({
				id: item.id,
				title: item.title,
				description: item.description,
				image: item.imageUrl,
				numStickersInCollection: item.numberOfTiles,
				available: item.available,
				completed: item.completed,
				claimed: item.diamondsClaimed,
				stickers: stickers,
				numStickersCollected: stickersCollected,
			});
		});

		groups.push({
			id: gr.id,
			name: gr.title,
			items: groupCollections,
		});
	});

	/** Exchange actions ***/
	const exchangeRatings: ExchangeDiamondsRating[] = [];
	if (diamondRatings.length > 0) {
		diamondRatings.map((item: ExchangeDiamondsRatingModel) => {
			exchangeRatings.push({
				id: item.id,
				receiveCoins: item.coins,
				costDiamonds: item.diamonds,
			});
		});
	}

	/** Autoclicker actions ***/
	const autoclickerRatings: AutoclickerDiamondsRating[] = [];
	if (diamondUnattended.length > 0) {
		diamondUnattended.map((item: AutoclickerDiamondsRatingModel) => {
			autoclickerRatings.push({
				id: item.id,
				hours: item.hours,
				costDiamonds: item.diamonds,
			});
		});
	}

	/** Skins **/
	const skins: Skin[] = [];
	if (skinsModel.length > 0) {
		skinsModel.map((item) => {
			const cover: string = `${item.url}cover.png`;
			const images: SkinImage[] = [];

			for (let index = 1; index <= levelsModel.levels.length; index++) {
				let url: string = '';
				if (item && item.url && typeof index !== 'undefined') {
					url = `${item.url}${index}.${item.imageType}`;
				} else {
					console.error('Invalid item or index.');
				}

				images.push({
					image: url,
				});
			}

			skins.push({
				id: item.id,
				name: item.name,
				cover: cover,
				price: item.diamondsPrice,
				owned: item.userPurchased,
				selected: item.skinSelected,
				expired: item.isExpired ? item.isExpired : false,
				images: images,
				imageExtension: item.imageType,
			});
		});
	}

	const userStatusData: UserStatus = {
		newCoinsCollected: generatedCoins,
		newDiamondsCollected: generatedDiamonds,
		previousCoins: previousCoins,
		diamonds: extraInfo.extraDiamonds.value,
		levelImageUrl: levelImageUrl,
		energyPerSecond: energyPerSecond,
		earnPerTap: extraInfo.earnPerTap?.value,
		profitPerHour: extraInfo.profitPerHour?.value,
		level: extraInfo.level?.value,
		currentCoins: extraInfo.currentCoins?.value,
		maxEnergy: extraInfo.maxEnergy?.value,
		currentEnergy: extraInfo.currentEnergy?.value,
		selectedNetworkId: extraInfo.selectedChain.value,
		categories: categories,
		rewards: rewards,
		youtubeRewards: youtubeRewards,
		dailyRewards: dailyRewards,
		levels: levels,
		products: products,
		email: extraInfo.vottunWorldUsername.value,
		totalCoinsEarned: extraInfo.totalCoinsEarned.value,
		pendingActions: pendingActions,
		collections: groups,
		showCollectionsTutorial: extraInfo.showCollectionsTutorial,
		friendsRewards: friendsRewardCoins,
		tonWallet: extraInfo.tonWallet,
		levelComboList: levelComboList,
		exchangeDiamondsRating: exchangeRatings,
		autoclickerDiamondsRating: autoclickerRatings,
		autoclickerTimerRemainingSeconds: extraInfo.unattendedEarnPerTapSeconds,
		vtnAmount: extraInfo.vtns,
		boostStars: {
			level: extraInfo.boostStars.value,
			nextLevelPrice: extraInfo.boostStars.nextLevelPrice,
		},
		boostEnergy: {
			level: extraInfo.boostEnergy.value,
			nextLevelPrice: extraInfo.boostEnergy.nextLevelPrice,
		},
		availableEnergyHack: {
			value: extraInfo.boostEnergyRefill.value,
			updateTimestamp: extraInfo.boostEnergyRefill.updateTimestamp,
		},
		gameConfig: {
			welcomePopup: gameConfig.welcomePopup,
			newFriendReward: gameConfig.newFriendReward,
			showOnboardingPageZero: gameConfig.showOnboardingPageZero,
		},
		skins: skins,
	};

	return userStatusData;
};
